import {
  ChangeDetectionStrategy,
  Component,
  Input,
  model,
  OnChanges,
  OnInit,
  output,
  OutputEmitterRef,
  SimpleChanges,
} from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import { InputText } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { DragDropModule } from 'primeng/dragdrop';
import { DynamicFilters } from '@everymatrix-base-components/utils';

@Component({
  selector: 'lib-report-configuration-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    DmButtonLibComponent,
    InputText,
    CheckboxModule,
    DragDropModule,
    FormsModule,
  ],
  templateUrl: './report-configuration.html',
  styleUrls: ['./report-configuration-modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportConfigurationModalComponent implements OnInit, OnChanges {
  visible = model(false);
  @Input()
  allAvailableFields!: DynamicFilters[];
  @Input()
  selectedList!: DynamicFilters[];
  searchQuery = '';
  fieldsEmitter: OutputEmitterRef<{
    fieldsToShow: DynamicFilters[];
    allFields: DynamicFilters[];
  }> = output();
  selectAll = false;
  dragData!: {
    target: HTMLElement;
    index: number;
  } | null;

  ngOnInit() {
    this.updateSelections();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedList']) {
      this.updateSelections();
    }
  }

  updateSelections(): void {
    this.selectAll =
      this.allAvailableFields.length === this.selectedList.length;
    const selectedIds = new Set(this.selectedList.map((item) => item.field_id));

    this.allAvailableFields = this.allAvailableFields.map((field) => ({
      ...field,
      selected: selectedIds.has(field.field_id),
    }));
  }

  protected get isAllVisibleSelected(): boolean {
    const visibleFields = this.filteredUnselectedFields.filter(
      (f) => !f.required
    );
    return visibleFields.every((field) =>
      this.selectedList.some((s) => s.field_id === field.field_id)
    );
  }

  protected selectVisibleFields(select: boolean): void {
    const visibleFields = this.filteredUnselectedFields.filter(
      (f) => !f.required
    );

    if (select) {
      const newSelections = visibleFields.filter(
        (f) => !this.selectedList.some((s) => s.field_id === f.field_id)
      );
      this.selectedList = [...this.selectedList, ...newSelections];
    } else {
      this.selectedList = this.selectedList.filter(
        (s) =>
          s.required || !visibleFields.some((f) => f.field_id === s.field_id)
      );
    }

    this.allAvailableFields = this.allAvailableFields.map((field) => {
      const isVisible = visibleFields.some(
        (f) => f.field_id === field.field_id
      );
      const isSelected = this.selectedList.some(
        (s) => s.field_id === field.field_id
      );
      return {
        ...field,
        selected: field.required || (isVisible ? select : isSelected),
      };
    });
  }

  protected onItemChange(field: DynamicFilters): void {
    const updatedSelected = field.selected
      ? [...this.selectedList, field]
      : this.selectedList.filter((item) => item.field_id !== field.field_id);

    this.selectedList = updatedSelected;

    this.selectAll = this.filteredUnselectedFields
      .filter((f) => !f.required)
      .every((f) => updatedSelected.some((sel) => sel.field_id === f.field_id));
  }

  protected onDragStart(event: DragEvent, item: DynamicFilters, i: number) {
    this.dragData = {
      target: event.target as HTMLElement,
      index: i,
    };
    this.dragData.target.classList.add('drag-opacity');
  }

  protected onDragEnter(index: number): void {
    if (this.dragData && index !== this.dragData.index) {
      const dragIndex = this.dragData.index;
      this.selectedList = this.selectedList.map((item, idx) =>
        idx === index
          ? this.selectedList[dragIndex]
          : idx === dragIndex
          ? this.selectedList[index]
          : item
      );
      this.dragData.index = index;
    }
  }

  protected onDragEnd(): void {
    if (this.dragData) {
      this.dragData.target.classList.remove('drag-opacity');
      this.dragData = null;
    }
  }

  protected removeListItem(field: DynamicFilters): void {
    this.selectedList = [
      ...this.selectedList.filter((item) => item.field_id !== field.field_id),
    ];
    this.allAvailableFields.forEach((item) => {
      if (item.field_id === field.field_id) {
        item.selected = false;
      }
    });
    this.selectAll = false;
  }

  protected resetToDefault(): void {
    this.selectedList = [
      ...this.allAvailableFields.filter((item) => item.is_default === 'true'),
    ];
    this.allAvailableFields.forEach((item) => {
      item.selected = item.is_default === 'true';
    });
  }

  protected get filteredUnselectedFields(): DynamicFilters[] {
    if (!this.searchQuery.trim()) {
      return this.allAvailableFields;
    }
    return this.allAvailableFields.filter((item) =>
      item.field_name.toLowerCase().includes(this.searchQuery.toLowerCase())
    );
  }

  protected apply(): void {
    this.visible.set(false);
    this.fieldsEmitter.emit({
      fieldsToShow: this.selectedList,
      allFields: this.allAvailableFields,
    });
  }
}
